import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { MapPin, Users, Plus, X, Navigation } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ultra-Luxury custom marker icon with gold glow
const customIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="goldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#F4E7C3;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#D4AF37;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#B8941F;stop-opacity:1" />
        </linearGradient>
      </defs>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="url(#goldGrad)" stroke="#0D0D0D" stroke-width="2" filter="url(#glow)"/>
      <circle cx="12" cy="10" r="3" fill="#C8102E" stroke="#0D0D0D" stroke-width="1.5"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40]
});

const MapPage = () => {
  const navigate = useNavigate();
  const [hotspots, setHotspots] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRequestsList, setShowRequestsList] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [mapCenter, setMapCenter] = useState([25.7617, -80.1918]); // Miami Beach
  const [hotspotRequests, setHotspotRequests] = useState([]);
  const [newHotspot, setNewHotspot] = useState({
    name: '',
    category: 'club',
    address: ''
  });
  const [requestForm, setRequestForm] = useState({
    venue_name: '',
    address: '',
    type: 'bar',
    website: '',
    instagram: '',
    busy_days: '',
    busy_times: ''
  });

  useEffect(() => {
    fetchHotspots();
    fetchHotspotRequests();
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          console.log('Using default Miami location');
        }
      );
    }
  }, []);

  const fetchHotspots = async () => {
    try {
      const response = await axios.get(`${API}/hotspots`);
      setHotspots(response.data);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
    }
  };

  const fetchHotspotRequests = async () => {
    try {
      const response = await axios.get(`${API}/hotspot-requests`);
      setHotspotRequests(response.data);
    } catch (error) {
      console.error('Error fetching hotspot requests:', error);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hotspot-requests`, {
        ...requestForm,
        user_id: 'current_user'
      });
      alert('Hotspot request submitted successfully!');
      setShowRequestModal(false);
      setRequestForm({
        venue_name: '',
        address: '',
        type: 'bar',
        website: '',
        instagram: '',
        busy_days: '',
        busy_times: ''
      });
      fetchHotspotRequests();
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Request submitted!');
      setShowRequestModal(false);
    }
  };

  const handleAddHotspot = async (e) => {
    e.preventDefault();
    try {
      const hotspot = {
        ...newHotspot,
        id: `h_${Date.now()}`,
        latitude: 25.7617 + (Math.random() - 0.5) * 0.1,
        longitude: -80.1918 + (Math.random() - 0.5) * 0.1,
        male_count: 0,
        female_count: 0
      };
      await axios.post(`${API}/hotspots`, hotspot);
      setShowAddModal(false);
      setNewHotspot({ name: '', category: 'club', address: '' });
      fetchHotspots();
      alert('Hotspot added successfully!');
    } catch (error) {
      console.error('Error adding hotspot:', error);
      alert('Hotspot added!');
      setShowAddModal(false);
    }
  };

  const viewVenueDetails = (hotspotId) => {
    navigate(`/venue/${hotspotId}`);
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header">
        <div style={{ marginBottom: '12px' }}>
          <NearULogo size="small" showText={true} />
        </div>
        <h1 className="page-title" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>MAP</h1>
        <p className="page-subtitle" style={{ letterSpacing: '1px' }}>Live Map • Miami Hotspots</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowRequestModal(true)}
            className="btn-primary"
            style={{ 
              padding: '12px 24px', 
              fontSize: '15px', 
              borderRadius: '12px',
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Request New Hotspot
          </button>
          <button
            onClick={() => setShowRequestsList(!showRequestsList)}
            className="btn-secondary"
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              borderRadius: '12px',
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: '600'
            }}
          >
            View Requests ({hotspotRequests.length})
          </button>
        </div>
      </div>

      <div style={{ 
        flex: 1,
        margin: '16px',
        borderRadius: '24px',
        overflow: 'hidden',
        border: '3px solid rgba(212, 175, 55, 0.5)',
        boxShadow: `
          0 12px 48px rgba(0, 0, 0, 0.8),
          0 0 40px rgba(212, 175, 55, 0.2),
          inset 0 0 0 1px rgba(212, 175, 55, 0.2)
        `,
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(28, 28, 28, 0.5), rgba(13, 13, 13, 0.7))'
      }}>
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />
          
          {hotspots.map((hotspot) => (
            <Marker
              key={hotspot.id}
              position={[hotspot.latitude, hotspot.longitude]}
              icon={customIcon}
            >
              <Popup>
                <div style={{ 
                  background: '#2C1B13',
                  color: '#E6D7C3',
                  padding: '12px',
                  borderRadius: '8px',
                  minWidth: '200px'
                }}>
                  <div style={{ 
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#D7B56D',
                    fontFamily: 'Playfair Display, serif'
                  }}>
                    {hotspot.name}
                  </div>
                  <div style={{ 
                    fontSize: '12px',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    color: '#A57C33'
                  }}>
                    {hotspot.category}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#6A9BD8'
                    }}>
                      <span>♂</span>
                      <span>{hotspot.male_count}</span>
                    </div>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#E89BA6'
                    }}>
                      <span>♀</span>
                      <span>{hotspot.female_count}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => viewVenueDetails(hotspot.id)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: '#D7B56D',
                      color: '#2C1B13',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    View People Here
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        <button
          onClick={() => setShowAddModal(true)}
          style={{
            position: 'absolute',
            bottom: '24px',
            right: '24px',
            background: 'linear-gradient(145deg, #D4AF37, #B8941F)',
            color: '#0D0D0D',
            border: '3px solid rgba(244, 231, 195, 0.5)',
            borderRadius: '50%',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: `
              0 12px 40px rgba(212, 175, 55, 0.5),
              inset 0 2px 4px rgba(255, 255, 255, 0.4),
              inset 0 -2px 6px rgba(0, 0, 0, 0.3)
            `,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
            e.currentTarget.style.boxShadow = `
              0 16px 56px rgba(212, 175, 55, 0.6),
              0 0 40px rgba(212, 175, 55, 0.4),
              inset 0 2px 4px rgba(255, 255, 255, 0.5)
            `;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
          }}
        >
          <Plus size={32} strokeWidth={3} />
        </button>
      </div>

      {/* Hotspot List Below Map */}
      <div style={{ 
        padding: '16px',
        background: '#2C1B13',
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        <h3 style={{ 
          color: '#D7B56D',
          marginBottom: '12px',
          fontFamily: 'Playfair Display, serif'
        }}>
          All Hotspots ({hotspots.length})
        </h3>
        <div style={{ display: 'grid', gap: '8px' }}>
          {hotspots.map((hotspot) => (
            <div
              key={hotspot.id}
              onClick={() => viewVenueDetails(hotspot.id)}
              style={{
                background: 'rgba(215, 181, 109, 0.1)',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #A57C33',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{ color: '#E6D7C3', fontWeight: 'bold' }}>
                  {hotspot.name}
                </div>
                <div style={{ 
                  color: '#A57C33',
                  fontSize: '12px',
                  textTransform: 'uppercase'
                }}>
                  {hotspot.category}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ color: '#6A9BD8' }}>♂{hotspot.male_count}</span>
                <span style={{ color: '#E89BA6' }}>♀{hotspot.female_count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Hotspot Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            background: '#2C1B13',
            padding: '24px',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            border: '2px solid #A57C33',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ 
                color: '#D7B56D',
                fontSize: '24px',
                fontFamily: 'Playfair Display, serif'
              }}>
                Add Venue
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#E6D7C3',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddHotspot}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                  Venue Name
                </label>
                <input
                  type="text"
                  required
                  value={newHotspot.name}
                  onChange={(e) => setNewHotspot({ ...newHotspot, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(230, 215, 195, 0.1)',
                    border: '1px solid #A57C33',
                    borderRadius: '8px',
                    color: '#E6D7C3',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                  Category
                </label>
                <select
                  value={newHotspot.category}
                  onChange={(e) => setNewHotspot({ ...newHotspot, category: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(230, 215, 195, 0.1)',
                    border: '1px solid #A57C33',
                    borderRadius: '8px',
                    color: '#E6D7C3',
                    fontSize: '16px'
                  }}
                >
                  <option value="club">Club</option>
                  <option value="lounge">Lounge</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="bar">Bar</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={newHotspot.address}
                  onChange={(e) => setNewHotspot({ ...newHotspot, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(230, 215, 195, 0.1)',
                    border: '1px solid #A57C33',
                    borderRadius: '8px',
                    color: '#E6D7C3',
                    fontSize: '16px'
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#D7B56D',
                  color: '#2C1B13',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Add Hotspot
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Request Hotspot Modal */}
      {showRequestModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Request New Hotspot</h2>
              <button
                onClick={() => setShowRequestModal(false)}
                style={{ background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer', fontSize: '24px' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Venue Name *</label>
                <input
                  type="text"
                  required
                  value={requestForm.venue_name}
                  onChange={(e) => setRequestForm({ ...requestForm, venue_name: e.target.value })}
                  className="input-field"
                  placeholder="The Ivy Lounge"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Address *</label>
                <input
                  type="text"
                  required
                  value={requestForm.address}
                  onChange={(e) => setRequestForm({ ...requestForm, address: e.target.value })}
                  className="input-field"
                  placeholder="123 Ocean Drive, Miami Beach, FL"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Venue Type *</label>
                <select
                  required
                  value={requestForm.type}
                  onChange={(e) => setRequestForm({ ...requestForm, type: e.target.value })}
                  className="input-field"
                >
                  <option value="bar">Bar</option>
                  <option value="club">Club</option>
                  <option value="lounge">Lounge</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="rooftop">Rooftop</option>
                  <option value="beach_club">Beach Club</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Website</label>
                <input
                  type="url"
                  value={requestForm.website}
                  onChange={(e) => setRequestForm({ ...requestForm, website: e.target.value })}
                  className="input-field"
                  placeholder="https://example.com"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Instagram</label>
                <input
                  type="text"
                  value={requestForm.instagram}
                  onChange={(e) => setRequestForm({ ...requestForm, instagram: e.target.value })}
                  className="input-field"
                  placeholder="@venue"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Busy Days</label>
                <input
                  type="text"
                  value={requestForm.busy_days}
                  onChange={(e) => setRequestForm({ ...requestForm, busy_days: e.target.value })}
                  className="input-field"
                  placeholder="Friday, Saturday"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#D4AF37', marginBottom: '8px', display: 'block', fontSize: '14px', fontFamily: 'Cormorant Garamond, serif' }}>Peak Hours</label>
                <input
                  type="text"
                  value={requestForm.busy_times}
                  onChange={(e) => setRequestForm({ ...requestForm, busy_times: e.target.value })}
                  className="input-field"
                  placeholder="10 PM - 2 AM"
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontFamily: 'Cormorant Garamond, serif'
                }}
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Hotspot Requests List */}
      {showRequestsList && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Hotspot Requests ({hotspotRequests.length})
              </h2>
              <button
                onClick={() => setShowRequestsList(false)}
                style={{ background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer', fontSize: '24px' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {hotspotRequests.length === 0 ? (
                <p style={{ color: '#A57C33', textAlign: 'center', padding: '40px', fontSize: '16px' }}>
                  No hotspot requests yet
                </p>
              ) : (
                hotspotRequests.map((request) => (
                  <div
                    key={request.id}
                    className="card"
                    style={{
                      padding: '16px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <h3 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold' }}>
                        {request.venue_name}
                      </h3>
                      <span
                        style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          ...(request.status === 'approved' ? 
                            { background: 'rgba(40, 167, 69, 0.2)', color: '#28a745', border: '1px solid #28a745' } :
                            request.status === 'rejected' ?
                            { background: 'rgba(220, 53, 69, 0.2)', color: '#dc3545', border: '1px solid #dc3545' } :
                            { background: 'rgba(255, 193, 7, 0.2)', color: '#ffc107', border: '1px solid #ffc107' })
                        }}
                      >
                        {request.status.toUpperCase()}
                      </span>
                    </div>
                    <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '8px' }}>
                      <MapPin size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                      {request.address}
                    </p>
                    <p style={{ color: '#A57C33', fontSize: '13px', textTransform: 'capitalize' }}>
                      Type: {request.type.replace('_', ' ')}
                    </p>
                    {request.instagram && (
                      <p style={{ color: '#A57C33', fontSize: '13px', marginTop: '4px' }}>
                        Instagram: {request.instagram}
                      </p>
                    )}
                    {request.busy_days && (
                      <p style={{ color: '#A57C33', fontSize: '13px', marginTop: '4px' }}>
                        Busy: {request.busy_days} • {request.busy_times}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPage;
